import React, { useEffect, useRef, useState, useCallback } from "react"
import "./index.css"
import { onDownloadReportPdf } from "./DownloadReport"
import zithasLogo from "../../../assets/images/zithasLogo.png"
import footerDesign from "../../../assets/images/appointment_footer.png"
import { Modal } from "reactstrap"
import { CKEditor } from "@ckeditor/ckeditor5-react"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import { dummyContent } from "./dummySeoReportData"

// ─── Constants ────────────────────────────────────────────────────────────────  ─
const PAGE_HEIGHT = 800 // must match .page-content-letter rendered height
const CONTENT_WIDTH = 684 // must match .page-content-letter rendered width
const DEBOUNCE_MS = 600 // wait this long after last keystroke before paginating

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Create a new element copying tag + className + style from source */
const cloneShell = node => {
  const el = document.createElement(node.tagName || "SPAN")
  if (node.className) el.className = node.className
  const style = node.getAttribute && node.getAttribute("style")
  if (style) el.setAttribute("style", style)
  return el
}

/**
 * Binary-search split of a leaf element's text across pages.
 *
 * Instead of probing word-by-word (O(n) DOM ops), we binary-search
 * for the last word index that still fits → O(log n) DOM ops per paragraph.
 */
const splitLeafByBinarySearch = (node, page, commitPage) => {
  const words = (node.textContent || "").trim().split(/\s+/).filter(Boolean)
  if (!words.length) return

  let start = 0

  while (start < words.length) {
    // Check: does even a single word overflow on an empty page?
    const probe = cloneShell(node)
    probe.textContent = words[start]
    page.appendChild(probe)
    const oneWordFits = page.scrollHeight <= PAGE_HEIGHT
    page.removeChild(probe)

    if (!oneWordFits) {
      // Word alone overflows (extremely long word / tiny PAGE_HEIGHT)
      commitPage()
      start++
      continue
    }

    // Binary search: find the largest `end` where words[start..end] fit
    let lo = start
    let hi = words.length - 1
    let best = start

    while (lo <= hi) {
      const mid = (lo + hi) >>> 1
      const probe = cloneShell(node)
      probe.textContent = words.slice(start, mid + 1).join(" ")
      page.appendChild(probe)
      const fits = page.scrollHeight <= PAGE_HEIGHT
      page.removeChild(probe)

      if (fits) {
        best = mid
        lo = mid + 1
      } else {
        hi = mid - 1
      }
    }

    // Commit the fitting slice as a real element
    const commitEl = cloneShell(node)
    commitEl.textContent = words.slice(start, best + 1).join(" ")
    page.appendChild(commitEl)

    start = best + 1

    if (start < words.length) commitPage()
  }
}

/**
 * Recursively place a DOM node onto the current page.
 */
const addNode = (node, page, commitPage) => {
  if (node.nodeType === Node.TEXT_NODE) {
    if (!node.textContent.trim()) return
    const span = document.createElement("span")
    span.textContent = node.textContent
    addNode(span, page, commitPage)
    return
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return

  const clone = node.cloneNode(true)
  page.appendChild(clone)
  if (page.scrollHeight <= PAGE_HEIGHT) return // fits ✓
  page.removeChild(clone)

  const hasChildElements = node.children && node.children.length > 0

  if (!hasChildElements) {
    splitLeafByBinarySearch(node, page, commitPage)
  } else {
    // Try on a fresh page first
    if (page.innerHTML.trim()) {
      commitPage()
      const freshClone = node.cloneNode(true)
      page.appendChild(freshClone)
      if (page.scrollHeight <= PAGE_HEIGHT) return // fits on fresh page ✓
      page.removeChild(freshClone)
    }
    // Still too tall — recurse into children
    Array.from(node.childNodes).forEach(child =>
      addNode(child, page, commitPage)
    )
  }
}

/**
 * Async paginator — yields to the browser between batches of top-level
 * nodes so the UI thread never freezes on large content.
 */
const paginateAsync = (html, container, onDone, cancelRef) => {
  container.innerHTML = ""

  const finalPages = []
  const page = document.createElement("div")
  container.appendChild(page)

  const commitPage = () => {
    const trimmed = page.innerHTML.trim()
    if (trimmed) finalPages.push(trimmed)
    page.innerHTML = ""
  }

  const temp = document.createElement("div")
  temp.innerHTML = html
  const topNodes = Array.from(temp.childNodes)

  let index = 0
  const BATCH = 5 // top-level nodes processed per frame

  const processNext = () => {
    if (cancelRef.current) return // stale run — abort

    const end = Math.min(index + BATCH, topNodes.length)
    for (let i = index; i < end; i++) {
      addNode(topNodes[i], page, commitPage)
    }
    index = end

    if (index < topNodes.length) {
      setTimeout(processNext, 0) // yield to browser, then continue
    } else {
      if (page.innerHTML.trim()) finalPages.push(page.innerHTML)
      onDone(finalPages)
    }
  }

  processNext()
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AppointmentPreview({ previewModal, setPreviewModal }) {
  const [loading, setLoading] = useState(false)
  const [pages, setPages] = useState([])
  const [paginating, setPaginating] = useState(false)

  const hiddenRef = useRef()
  const reportRef = useRef()
  const debounceTimer = useRef(null)
  const cancelRef = useRef(false)

  const [content, setContent] = useState("")

  const schedulePagination = useCallback(html => {
    // Cancel any in-progress or pending run
    cancelRef.current = true
    clearTimeout(debounceTimer.current)

    debounceTimer.current = setTimeout(() => {
      if (!hiddenRef.current) return
      cancelRef.current = false
      setPaginating(true)

      paginateAsync(
        html,
        hiddenRef.current,
        result => {
          if (cancelRef.current) return
          setPages(result)
          setPaginating(false)
        },
        cancelRef
      )
    }, DEBOUNCE_MS)
  }, [])

  useEffect(() => {
    schedulePagination(content)
    return () => {
      clearTimeout(debounceTimer.current)
      cancelRef.current = true
    }
  }, [content, schedulePagination])

  useEffect(() => {
    if (previewModal && !content) setContent(dummyContent)
  }, [previewModal])

  return (
    <Modal isOpen={previewModal} centered className="modal-fullscreen">
      <div className="modal-header">
        <h5 className="modal-title mt-0">Appointment Letter - Jack Smith</h5>
        <div>
          <button
            onClick={() =>
              onDownloadReportPdf(setLoading, reportRef, "Appointment_Letter")
            }
            className="btn btn-success me-3"
            disabled={loading || paginating}
          >
            <i
              className={`fas fa-${
                loading ? "spinner bx-spin" : "download"
              } me-2`}
            ></i>
            {paginating || loading ? "Processing..." : "Download"}
          </button>
          <button
            type="button"
            onClick={() => setPreviewModal(false)}
            className="btn-modal-close"
          >
            <span className="btn-modal-span">&times;</span>
          </button>
        </div>
      </div>

      <div className="modal-body preview-body">
        <div className="row">
          <div className="col-md-5">
            {/* Editor */}
            <div className="p-4 bg-white mb-5 ">
              <CKEditor
                editor={ClassicEditor}
                data={content}
                onChange={(_event, editor) => setContent(editor.getData())}
              />
            </div>
          </div>

          <div className="col-md-7 overflow-auto position-relative" style={{height: '80vh'}} >
            {/* Pages */}
            <div ref={reportRef} >
              {/* Loader */}
              {paginating && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(255,255,255,0.65)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                    minHeight: 200,
                    borderRadius: 4,
                  }}
                >
                  <i className="fas fa-spinner bx-spin fa-2x text-secondary" />
                </div>
              )}

              {pages.map((pageHtml, i) => (
                <div className="a4-page" key={i}>
                  {/* HEADER */}
                  {/* <div className="page-header">
                <div className="header-banner">
                  <div className="header-logo-wrap">
                    <div className="header-logo-box">
                      <img src={zithasLogo} alt="logo" />
                    </div>
                    <div>
                      <div className="header-company-name">Zithas Technologies</div>
                      <div className="header-company-sub">Est. 2015 · Vadodara, India</div>
                    </div>
                  </div>
                  <div className="header-doc-badge">Appointment Letter</div>
                </div>
                <div className="header-accent-line"></div>
              </div> */}

                  <div className="page-header pt-3 ps-4">
                    <img
                      src={zithasLogo}
                      alt=""
                      className="bg-white"
                      style={{ height: "55px" }}
                    />
                  </div>

                  {/* CONTENT */}
                  <div
                    className="page-content-letter ckeditor-content"
                    dangerouslySetInnerHTML={{ __html: pageHtml }}
                  />
                  {/* FOOTER */}
                  <div className="page-footer">
                    <img src={footerDesign} alt="" className="w-100" />
                    {/* <div className="footer-accent-line"></div> */}
                    <div className="footer-body">
                      <div className="footer-info">
                        <span>
                          {" "}
                          <i className="fa fa-envelope me-1"></i> hr@zithas.com
                        </span>
                        <span>
                          {" "}
                          <i className="fa fa-globe me-1"></i> www.zithas.com
                        </span>
                        <span>
                          {" "}
                          <i className="fa fa-phone me-1"></i> +91 265 000 0000
                        </span>
                      </div>
                      {/* <div className="footer-page-num">{i + 1}</div> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/*
          Hidden measurement container.
          ⚠️ Width MUST match the actual rendered width of .page-content-letter.
        */}
        <div
          ref={hiddenRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            visibility: "hidden",
            pointerEvents: "none",
            width: `${CONTENT_WIDTH}px`,
            overflow: "visible",
          }}
        />
      </div>
    </Modal>
  )
}
